import React from 'react';
import { usePreloadedQuery, type PreloadedQuery, graphql } from 'react-relay';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import { FieldsCompactModeProvider } from '@atlassian/jira-issue-view-layout-templates-compact-mode-provider';
import { useProjectKey } from '@atlassian/jira-project-context-service';
import type { panelViewCustomerQuery } from '@atlassian/jira-relay/src/__generated__/panelViewCustomerQuery.graphql';
import { ThrowOnQueryError } from '@atlassian/jira-servicedesk-customer-service-common';
import { CustomerOrganizations } from '../../../common/ui/customer-organizations';
import { DetailsView, Divider, getEditDetailsUrlByEntityType } from '../../../common/ui/details';
import { DetailsContainer } from '../../../common/ui/details-container';
import { customerServiceIssueViewPanelCustomerExperience } from '../../../experiences';

export const DIVIDER_TESTID =
	'servicedesk-customer-service-issue-view-panel.ui.customer.customer-details.divider';

type Props = {
	queryReference: PreloadedQuery<panelViewCustomerQuery>;
	orgCount?: number;
};

const Inner = ({ queryReference, orgCount }: Props) => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const {
		customerService: { individualByAccountId },
	} = usePreloadedQuery<panelViewCustomerQuery>(
		graphql`
			query panelViewCustomerQuery(
				$cloudId: ID!
				$accountId: ID!
				$useReporterOrganizations: Boolean!
				$includePlatformValue: Boolean!
			) {
				customerService(cloudId: $cloudId) @required(action: THROW) {
					individualByAccountId(accountId: $accountId, filter: { context: { type: ISSUE } })
						@required(action: THROW) {
						__typename
						... on CustomerServiceIndividual {
							id
							details {
								...detailsView_servicedeskCustomerServiceIssueViewPanel
									@arguments(includePlatformValue: $includePlatformValue)
							}
							organizations @include(if: $useReporterOrganizations) {
								...customerOrganizations_servicedeskCustomerServiceIssueViewPanel_CustomerOrganization
							}
						}
						... on QueryError {
							...throwOnQueryError_servicedeskCustomerServiceCommon
						}
					}
				}
			}
		`,
		queryReference,
	);

	if (individualByAccountId.__typename !== 'CustomerServiceIndividual') {
		return (
			<ThrowOnQueryError
				operationName="panelViewCustomerQuery"
				errorRef={
					individualByAccountId.__typename === 'QueryError' ? individualByAccountId : undefined
				}
			/>
		);
	}

	const { details, organizations } = individualByAccountId;

	return (
		<>
			<DetailsView
				entityType="CUSTOMER"
				editDetailsUrl={getEditDetailsUrlByEntityType(
					projectKey,
					'CUSTOMER',
					individualByAccountId.id,
				)}
				details={details}
				entityExperience={customerServiceIssueViewPanelCustomerExperience}
			/>
			{orgCount === 0 ? (
				<CustomerOrganizations organizations={organizations || []} />
			) : (
				<Divider data-testid={DIVIDER_TESTID} />
			)}
		</>
	);
};

export const PanelView = (props: Props) => (
	<DetailsContainer
		context="CUSTOMER"
		entityExperience={customerServiceIssueViewPanelCustomerExperience}
	>
		<FieldsCompactModeProvider>
			<Inner {...props} />
		</FieldsCompactModeProvider>
	</DetailsContainer>
);
